import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { supabase } from "../supabase";
import roster from "../rosterData";

const F = "'Helvetica Neue', Helvetica, Arial, sans-serif";
const STORE_PASSWORD = "sighmadethissite";
const STORAGE_KEY = "yen_merch_unlocked";

/* ── helpers ── */
function fmt(price, currency) {
  if (!price) return null;
  return new Intl.NumberFormat("en-IL", {
    style: "currency",
    currency: currency || "ILS",
    maximumFractionDigits: 0,
  }).format(price);
}

function getCountdown(lockedUntil) {
  const target = new Date(lockedUntil + "T00:00:00+02:00");
  const diff = target - new Date();
  if (diff <= 0) return null;
  return {
    d: Math.floor(diff / 86400000),
    h: Math.floor((diff % 86400000) / 3600000),
    m: Math.floor((diff % 3600000) / 60000),
    s: Math.floor((diff % 60000) / 1000),
  };
}

/* ── SVG lock icon ── */
function LockIcon({ size = 28 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="rgba(240,237,232,0.7)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

/* ── Countdown ── */
function CountdownBadge({ lockedUntil, compact }) {
  const [cd, setCd] = useState(() => getCountdown(lockedUntil));
  useEffect(() => {
    const id = setInterval(() => setCd(getCountdown(lockedUntil)), 1000);
    return () => clearInterval(id);
  }, [lockedUntil]);
  if (!cd) return null;
  if (compact) return (
    <span style={{ fontFamily: "monospace", fontSize: "9px", color: "rgba(240,237,232,0.35)", letterSpacing: "0.04em" }}>
      {cd.d}d {String(cd.h).padStart(2, "0")}h {String(cd.m).padStart(2, "0")}m {String(cd.s).padStart(2, "0")}s
    </span>
  );
  return (
    <div style={{ display: "flex", gap: "12px", justifyContent: "center" }}>
      {[["d", cd.d], ["h", cd.h], ["m", cd.m], ["s", cd.s]].map(([unit, val]) => (
        <div key={unit} style={{ textAlign: "center" }}>
          <div style={{ fontFamily: "monospace", fontSize: "20px", fontWeight: 700, color: "rgba(240,237,232,0.8)", lineHeight: 1 }}>
            {String(val).padStart(2, "0")}
          </div>
          <div style={{ fontFamily: F, fontSize: "7px", letterSpacing: "0.2em", color: "rgba(240,237,232,0.25)", marginTop: "4px", textTransform: "uppercase" }}>{unit}</div>
        </div>
      ))}
    </div>
  );
}

/* ── Tag pill ── */
function Tag({ label }) {
  return (
    <span style={{
      fontFamily: F, fontSize: "7px", fontWeight: 700,
      letterSpacing: "0.18em", textTransform: "uppercase",
      padding: "3px 7px",
      border: "1px solid rgba(240,237,232,0.18)",
      color: "rgba(240,237,232,0.45)",
      display: "inline-block",
    }}>{label}</span>
  );
}

/* ── Digital Download Modal ── */
function DigitalDownloadModal({ item, onConfirm, onCancel }) {
  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 9999,
      background: "rgba(0,0,0,0.88)", backdropFilter: "blur(6px)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: "24px",
    }} onClick={onCancel}>
      <div onClick={e => e.stopPropagation()} style={{
        background: "#0a0a0a", border: "1px solid rgba(240,237,232,0.12)",
        maxWidth: "400px", width: "100%", padding: "44px 36px",
        textAlign: "center",
      }}>
        <p style={{ fontFamily: F, fontSize: "7px", fontWeight: 700, letterSpacing: "0.45em", textTransform: "uppercase", color: "rgba(240,237,232,0.3)", marginBottom: "24px" }}>
          Digital Download
        </p>
        <p style={{ fontFamily: F, fontSize: "16px", fontWeight: 900, letterSpacing: "-0.01em", color: "#f0ede8", lineHeight: 1.2, marginBottom: "16px" }}>
          {item.name}
        </p>
        <div style={{ width: "32px", height: "1px", background: "rgba(240,237,232,0.12)", margin: "0 auto 24px" }} />
        <p style={{ fontFamily: F, fontSize: "11px", fontWeight: 700, color: "#f0ede8", letterSpacing: "0.04em", lineHeight: 1.8, marginBottom: "8px" }}>
          THIS IS A DIGITAL DOWNLOAD<br />NOT A PHYSICAL PRODUCT
        </p>
        <p style={{ fontFamily: F, fontSize: "11px", color: "rgba(240,237,232,0.45)", lineHeight: 1.7, marginBottom: "32px" }}>
          You will be forwarded to Bandcamp to complete your purchase. After purchasing you will receive a download link via email.
        </p>
        <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
          <button onClick={onCancel} style={{
            fontFamily: F, fontSize: "8px", fontWeight: 700, letterSpacing: "0.2em", textTransform: "uppercase",
            padding: "10px 20px", border: "1px solid rgba(240,237,232,0.18)", background: "transparent",
            color: "rgba(240,237,232,0.4)", cursor: "pointer",
          }}>Cancel</button>
          <button onClick={onConfirm} style={{
            fontFamily: F, fontSize: "8px", fontWeight: 700, letterSpacing: "0.2em", textTransform: "uppercase",
            padding: "10px 24px", border: "1px solid #f0ede8", background: "#f0ede8",
            color: "#000", cursor: "pointer",
          }}>Continue to Bandcamp</button>
        </div>
      </div>
    </div>
  );
}

/* ── Merch Card ── */
function MerchCard({ item, index, variant }) {
  // variant: "hero" | "yen" | "artist"
  const [hovered, setHovered] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const isLocked   = item.locked_until && getCountdown(item.locked_until) !== null;
  const isSoldOut  = item.sold_out;
  const isDisabled = isLocked || isSoldOut;
  const isDigital  = item.badge === "Digital Download";
  const isYen      = variant === "hero" || variant === "yen";

  const tags = useMemo(() => {
    if (!item.tags) return [];
    if (Array.isArray(item.tags)) return item.tags;
    try { return JSON.parse(item.tags); } catch { return []; }
  }, [item.tags]); // eslint-disable-line

  function handleShopClick(e) {
    if (isDigital) { e.preventDefault(); setShowModal(true); }
  }
  function confirmRedirect() {
    setShowModal(false);
    window.open(item.shopify_url, "_blank", "noopener,noreferrer");
  }

  const btnBase = {
    fontFamily: F, fontSize: "8px", fontWeight: 700, letterSpacing: "0.22em",
    textTransform: "uppercase", padding: "8px 18px",
    cursor: "pointer", textDecoration: "none", display: "inline-block",
    whiteSpace: "nowrap",
    transition: "background 0.15s, color 0.15s, border-color 0.15s",
  };
  const btnFilled  = { ...btnBase, border: "1px solid #f0ede8", background: hovered ? "#d8d5d0" : "#f0ede8", color: "#000", borderColor: hovered ? "#d8d5d0" : "#f0ede8" };
  const btnOutline = { ...btnBase, border: "1px solid rgba(240,237,232,0.35)", background: hovered ? "rgba(240,237,232,0.06)" : "transparent", color: "#f0ede8", borderColor: hovered ? "rgba(240,237,232,0.7)" : "rgba(240,237,232,0.35)" };

  return (
    <>
      {showModal && <DigitalDownloadModal item={item} onConfirm={confirmRedirect} onCancel={() => setShowModal(false)} />}
      <div
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{ animation: `yen-merch-in 0.45s ${(index % 8) * 0.06}s ease both` }}
      >
        {/* Image */}
        <div style={{ position: "relative", overflow: "hidden", background: "#000", aspectRatio: "1" }}>
          {item.image_url && (
            <img src={item.image_url} alt={item.name} loading="lazy" style={{
              width: "100%", height: "100%", objectFit: "cover", display: "block",
              transform: hovered && !isDisabled ? "scale(1.04)" : "scale(1)",
              filter: isLocked
                ? "brightness(0.22) grayscale(1)"
                : isSoldOut
                  ? "brightness(0.4) grayscale(0.7)"
                  : hovered ? "brightness(1)" : "brightness(0.86)",
              transition: "transform 0.55s cubic-bezier(0.4,0,0.2,1), filter 0.3s ease",
            }} />
          )}
          {isSoldOut && (
            <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.45)" }}>
              <span style={{ fontFamily: F, fontSize: "9px", fontWeight: 700, letterSpacing: "0.3em", textTransform: "uppercase", color: "#f0ede8", border: "1px solid rgba(240,237,232,0.45)", padding: "5px 12px", background: "rgba(0,0,0,0.7)" }}>SOLD OUT</span>
            </div>
          )}
          {isLocked && (
            <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.55)", gap: "12px", padding: "16px" }}>
              <LockIcon size={24} />
              <CountdownBadge lockedUntil={item.locked_until} compact={false} />
            </div>
          )}
          {isDigital && !isLocked && !isSoldOut && (
            <div style={{ position: "absolute", top: "10px", left: "10px", fontFamily: F, fontSize: "7px", fontWeight: 700, letterSpacing: "0.28em", textTransform: "uppercase", padding: "3px 8px", border: "1px solid rgba(240,237,232,0.45)", color: "#f0ede8", background: "rgba(0,0,0,0.75)" }}>
              DIGITAL DOWNLOAD
            </div>
          )}
        </div>

        {/* Info */}
        <div style={{ paddingTop: "12px", paddingBottom: "20px" }}>
          {item.drop_label && (
            <p style={{ fontFamily: F, fontSize: "7px", fontWeight: 700, letterSpacing: "0.35em", textTransform: "uppercase", color: "rgba(240,237,232,0.22)", marginBottom: "4px" }}>
              {item.drop_label}
            </p>
          )}
          {!isYen && item.artist && (
            <p style={{ fontFamily: F, fontSize: "8px", letterSpacing: "0.18em", textTransform: "uppercase", color: "rgba(240,237,232,0.28)", marginBottom: "4px" }}>
              {item.artist}
            </p>
          )}
          <h3 style={{
            fontFamily: F,
            fontSize: "13px",
            fontWeight: 700, color: "#f0ede8",
            marginBottom: "10px",
            lineHeight: 1.3, letterSpacing: "-0.01em",
          }}>{item.name}</h3>

          {tags.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "5px", marginBottom: "10px" }}>
              {tags.map(t => <Tag key={t} label={t} />)}
            </div>
          )}

          {isDigital && !isDisabled && (
            <p style={{ fontFamily: F, fontSize: "9px", color: "rgba(240,237,232,0.28)", letterSpacing: "0.06em", marginBottom: "10px" }}>
              Instant download after purchase
            </p>
          )}

          {/* Price */}
          {isLocked ? (
            <CountdownBadge lockedUntil={item.locked_until} compact={true} />
          ) : isSoldOut ? (
            <span style={{ fontFamily: F, fontSize: "9px", color: "rgba(240,237,232,0.28)", letterSpacing: "0.15em", textTransform: "uppercase", display: "block", marginBottom: "10px" }}>Sold out</span>
          ) : item.price ? (
            <p style={{ fontFamily: F, fontSize: "11px", fontWeight: 300, color: "rgba(240,237,232,0.4)", letterSpacing: "0.02em", marginBottom: "12px" }}>
              {fmt(item.price, item.currency)}
            </p>
          ) : null}

          {/* Button — always left-aligned, fixed width, never stretches */}
          {!isDisabled && item.shopify_url && (
            <a
              href={item.shopify_url}
              target="_blank"
              rel="noopener noreferrer"
              style={isYen ? btnFilled : btnOutline}
              onClick={handleShopClick}
            >
              {isDigital ? "DOWNLOAD" : "SHOP"}
            </a>
          )}
        </div>
      </div>
    </>
  );
}

/* ── Hero: 2 featured YEN products ── */
function HeroSection({ items }) {
  if (!items.length) return null;
  const featured = items.slice(0, 2);
  return (
    <div style={{ padding: "40px 20px 56px", maxWidth: "1000px", margin: "0 auto" }}>
      <div style={{ textAlign: "center", marginBottom: "52px" }}>
        <p style={{
          fontFamily: F, fontSize: "10px", fontWeight: 700,
          letterSpacing: "0.45em", textTransform: "uppercase",
          color: "rgba(240,237,232,0.28)", marginBottom: "10px",
        }}>YEN SOUND</p>
        <h1 style={{
          fontFamily: F, fontSize: "clamp(28px, 5vw, 48px)", fontWeight: 900,
          letterSpacing: "-0.03em", color: "#f0ede8", lineHeight: 1,
        }}>DROP 001</h1>
      </div>
      <div className="merch-hero-grid" style={{
        display: "flex", justifyContent: "center", gap: "clamp(24px, 4vw, 56px)",
        flexWrap: "wrap",
      }}>
        {featured.map((item, i) => (
          <div key={item.id} style={{ width: "clamp(220px, 28vw, 320px)", flexShrink: 0 }}>
            <MerchCard item={item} index={i} variant="hero" />
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── YEN core section ── */
function YenCoreSection({ items }) {
  if (!items.length) return null;
  return (
    <div style={{ padding: "0 32px 64px", maxWidth: "1200px", margin: "0 auto" }}>
      <p style={{
        fontFamily: F, fontSize: "8px", fontWeight: 700, letterSpacing: "0.4em",
        textTransform: "uppercase", color: "rgba(240,237,232,0.2)", marginBottom: "32px",
        textAlign: "center",
      }}>
        YEN SOUND \u2014 CORE COLLECTION
      </p>
      <div className="merch-yen-grid" style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
        gap: "40px 28px",
      }}>
        {items.map((item, i) => (
          <MerchCard key={item.id} item={item} index={i} variant="yen" />
        ))}
      </div>
    </div>
  );
}

/* ── Artist filter ── */
function ArtistFilter({ artists, active, onSelect }) {
  if (artists.length < 2) return null;
  return (
    <div style={{
      display: "flex", justifyContent: "center", flexWrap: "wrap",
      gap: "16px 20px", padding: "16px 24px 32px",
    }}>
      {/* All pill */}
      <button onClick={() => onSelect(null)} style={{
        background: "none", border: "none", cursor: "pointer",
        display: "flex", flexDirection: "column", alignItems: "center", gap: "8px", padding: 0,
      }}>
        <div style={{
          width: "48px", height: "48px", borderRadius: "50%",
          border: active === null ? "2px solid #f0ede8" : "2px solid rgba(240,237,232,0.15)",
          background: "rgba(240,237,232,0.06)",
          display: "flex", alignItems: "center", justifyContent: "center",
          transition: "border-color 0.2s",
        }}>
          <span style={{ fontFamily: F, fontSize: "7px", fontWeight: 700, letterSpacing: "0.2em", color: active === null ? "#f0ede8" : "rgba(240,237,232,0.4)", textTransform: "uppercase" }}>ALL</span>
        </div>
        <span style={{ fontFamily: F, fontSize: "7px", letterSpacing: "0.15em", textTransform: "uppercase", color: active === null ? "#f0ede8" : "rgba(240,237,232,0.35)", fontWeight: active === null ? 700 : 400 }}>All</span>
      </button>

      {artists.map(a => {
        const rArtist = roster.find(r =>
          r.displayName === a || r.name === a || (r.aliases || []).includes(a)
        );
        const img = rArtist?.image || null;
        const isActive = active === a;
        return (
          <button key={a} onClick={() => onSelect(isActive ? null : a)} style={{
            background: "none", border: "none", cursor: "pointer",
            display: "flex", flexDirection: "column", alignItems: "center", gap: "8px", padding: 0,
          }}>
            <div style={{
              width: "48px", height: "48px", borderRadius: "50%", overflow: "hidden",
              border: isActive ? "2px solid #f0ede8" : "2px solid rgba(240,237,232,0.15)",
              background: "#0a0a0a", flexShrink: 0, transition: "border-color 0.2s",
            }}>
              {img
                ? <img src={img} alt={a} style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "top", display: "block", filter: isActive ? "brightness(1)" : "brightness(0.6)", transition: "filter 0.2s" }} />
                : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <span style={{ fontFamily: F, fontSize: "7px", fontWeight: 700, color: "rgba(240,237,232,0.4)" }}>{a[0]}</span>
                  </div>
              }
            </div>
            <span style={{ fontFamily: F, fontSize: "7px", letterSpacing: "0.15em", textTransform: "uppercase", color: isActive ? "#f0ede8" : "rgba(240,237,232,0.35)", fontWeight: isActive ? 700 : 400, whiteSpace: "nowrap" }}>{a}</span>
          </button>
        );
      })}
    </div>
  );
}

/* ── Artist merch section ── */
function ArtistSection({ items, artists, activeArtist, onSelectArtist }) {
  if (!items.length) return null;
  const filtered = activeArtist ? items.filter(i => i.artist === activeArtist) : items;
  return (
    <div style={{ padding: "0 32px 80px", maxWidth: "1200px", margin: "0 auto" }}>
      <p style={{
        fontFamily: F, fontSize: "8px", fontWeight: 700, letterSpacing: "0.4em",
        textTransform: "uppercase", color: "rgba(240,237,232,0.2)", marginBottom: "28px",
        textAlign: "center",
      }}>
        ARTIST MERCH
      </p>
      <ArtistFilter artists={artists} active={activeArtist} onSelect={onSelectArtist} />
      {filtered.length > 0 ? (
        <div className="merch-artist-grid" style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
          gap: "40px 24px",
        }}>
          {filtered.map((item, i) => (
            <MerchCard key={item.id} item={item} index={i} variant="artist" />
          ))}
        </div>
      ) : (
        <p style={{ fontFamily: F, fontSize: "9px", letterSpacing: "0.25em", textTransform: "uppercase", opacity: 0.18, textAlign: "center", padding: "40px 0" }}>
          No items.
        </p>
      )}
    </div>
  );
}


/* ── Ashdod Hero Banner ── */
function AshdodHero() {
  const [hovered, setHovered] = useState(false);
  return (
    <div style={{
      borderBottom: "1px solid rgba(240,237,232,0.07)",
      padding: "56px 24px 60px",
      textAlign: "center",
      display: "flex", flexDirection: "column", alignItems: "center", gap: "32px",
    }}>
      <div style={{ width: "120px", height: "120px" }}>
        <img
          src="https://i.imgur.com/v44wVQV.gif"
          alt="Made in Ashdod"
          style={{ width: "100%", height: "100%", objectFit: "contain" }}
        />
      </div>
      <Link
        to="/ashdod"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          fontFamily: F, fontSize: "8px", fontWeight: 700, letterSpacing: "0.4em",
          textTransform: "uppercase", padding: "12px 36px",
          border: "1px solid rgba(240,237,232,0.35)",
          background: hovered ? "rgba(240,237,232,0.07)" : "transparent",
          borderColor: hovered ? "rgba(240,237,232,0.65)" : "rgba(240,237,232,0.35)",
          color: "#f0ede8", textDecoration: "none", display: "inline-block",
          transition: "background 0.2s, border-color 0.2s",
        }}
      >
        ENTER
      </Link>
    </div>
  );
}

/* ── Marquee ── */
function MarqueeItem({ url, failed, onError }) {
  if (failed) {
    return (
      <span style={{
        fontFamily: F, fontSize: "clamp(36px, 6vw, 72px)", fontWeight: 900,
        fontStyle: "italic", letterSpacing: "-0.02em", color: "#f0ede8",
        whiteSpace: "nowrap", paddingRight: "80px", userSelect: "none", display: "inline-block",
      }}>
        YEN SOUND MERCH STORE
      </span>
    );
  }
  return (
    <img
      src={url}
      alt="YEN SOUND MERCH STORE"
      onError={onError}
      style={{
        height: "clamp(48px, 7vw, 90px)", width: "auto", display: "inline-block",
        filter: "invert(1)", flexShrink: 0, marginRight: "80px",
        userSelect: "none", pointerEvents: "none",
      }}
    />
  );
}

function Marquee() {
  const LOGO_URL = "https://raw.githubusercontent.com/davidslagorsky/yen-sound-site/main/MERCH%20STORE.png";
  const [imgFailed, setImgFailed] = useState(false);
  const copies = [0, 1, 2, 3, 4, 5, 6, 7];

  return (
    <div style={{ overflow: "hidden", width: "100%", padding: "20px 0", borderBottom: "1px solid rgba(240,237,232,0.08)" }}>
      <style>{`
        @keyframes yen-marquee {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .yen-marquee-track {
          display: flex;
          align-items: center;
          width: max-content;
          will-change: transform;
          animation: yen-marquee 24s linear infinite;
          pointer-events: none;
          user-select: none;
          -webkit-user-select: none;
        }
        @media (max-width: 600px) {
          .yen-marquee-track { animation-duration: 12s; }
        }
      `}</style>
      <div className="yen-marquee-track">
        {copies.map(i => (
          <MarqueeItem key={i} url={LOGO_URL} failed={imgFailed} onError={() => setImgFailed(true)} />
        ))}
      </div>
    </div>
  );
}

/* ── Gate ── */
function MerchGate({ onUnlock }) {
  const [pw, setPw] = useState("");
  const [err, setErr] = useState(false);
  function attempt() {
    if (pw.trim().toLowerCase() === STORE_PASSWORD) {
      try { localStorage.setItem(STORAGE_KEY, "1"); } catch {}
      onUnlock();
    } else { setErr(true); setTimeout(() => setErr(false), 1500); }
  }
  return (
    <div style={{ minHeight: "100vh", background: "#000", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "28px", padding: "40px" }}>
      <div style={{ textAlign: "center" }}>
        <p style={{ fontFamily: F, fontSize: "9px", fontWeight: 700, letterSpacing: "0.45em", textTransform: "uppercase", color: "rgba(240,237,232,0.3)", marginBottom: "10px" }}>YEN SOUND</p>
        <p style={{ fontFamily: F, fontSize: "28px", fontWeight: 900, letterSpacing: "-0.02em", color: "#f0ede8" }}>MERCH STORE</p>
      </div>
      <LockIcon size={28} />
      <p style={{ fontFamily: F, fontSize: "9px", letterSpacing: "0.3em", textTransform: "uppercase", color: "rgba(240,237,232,0.25)" }}>
        Store access restricted
      </p>
      <div style={{ display: "flex", gap: "8px", width: "100%", maxWidth: "280px" }}>
        <input type="password" value={pw} onChange={e => setPw(e.target.value)} onKeyDown={e => e.key === "Enter" && attempt()}
          placeholder="Password" autoFocus
          style={{ flex: 1, background: "transparent", border: err ? "1px solid rgba(255,100,100,0.6)" : "1px solid rgba(240,237,232,0.18)", color: "#f0ede8", fontFamily: F, fontSize: "12px", padding: "10px 14px", outline: "none" }} />
        <button onClick={attempt} style={{ fontFamily: F, fontSize: "8px", fontWeight: 700, letterSpacing: "0.2em", textTransform: "uppercase", padding: "10px 16px", border: "1px solid rgba(240,237,232,0.2)", background: "transparent", color: "#f0ede8", cursor: "pointer" }}>Enter</button>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════
   MAIN
══════════════════════════════════════════ */
export default function Merch() {
  const [items, setItems]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [storeLocked, setStoreLocked] = useState(false);
  const [pageUnlocked, setPageUnlocked] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) === "1"; } catch { return false; }
  });
  const [activeArtist, setActiveArtist] = useState(null);

  useEffect(() => {
    async function load() {
      const [{ data: settings }, { data }] = await Promise.all([
        supabase.from("site_settings").select("value").eq("key", "merch_locked").single(),
        supabase.from("merch").select("*").eq("active", true).order("sort_order", { ascending: true }),
      ]);
      if (settings?.value === true || settings?.value === "true") setStoreLocked(true);
      setItems(data || []);
      setLoading(false);
    }
    load();
  }, []);

  const yenItems    = useMemo(() => items.filter(i => i.artist === "YEN SOUND" || i.artist_slug === "yensound"), [items]);
  const artistItems = useMemo(() => items.filter(i => i.artist !== "YEN SOUND" && i.artist_slug !== "yensound"), [items]);
  const heroItems   = useMemo(() => yenItems.slice(0, 2), [yenItems]);
  const coreItems   = useMemo(() => yenItems.slice(2), [yenItems]);
  const artists     = useMemo(() => {
    const names = new Set();
    artistItems.forEach(i => { if (i.artist) names.add(i.artist); });
    return Array.from(names).sort();
  }, [artistItems]);

  if (storeLocked && !pageUnlocked) return <MerchGate onUnlock={() => setPageUnlocked(true)} />;

  return (
    <>
      <Helmet>
        <title>Merch \u2014 YEN SOUND</title>
        <meta name="description" content="Official merch from YEN SOUND artists." />
      </Helmet>

      <style>{`
        @keyframes yen-merch-in {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes ashdod-hero-spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        @media (max-width: 640px) {
          .merch-hero-grid > div  { width: calc(50% - 10px) !important; min-width: 140px !important; }
          .merch-hero-grid        { gap: 12px !important; padding: 0 !important; }
          .merch-yen-grid         { grid-template-columns: repeat(2, 1fr) !important; gap: 28px 12px !important; }
          .merch-artist-grid      { grid-template-columns: repeat(2, 1fr) !important; gap: 28px 12px !important; }
        }
      `}</style>

      <div style={{ backgroundColor: "#000", minHeight: "100vh", paddingTop: "60px" }}>
        <AshdodHero />
        <Marquee />
        {loading ? (
          <p style={{ fontFamily: F, fontSize: "9px", letterSpacing: "0.3em", textTransform: "uppercase", opacity: 0.18, padding: "120px 32px", textAlign: "center" }}>
            Loading...
          </p>
        ) : (
          <>
            {heroItems.length > 0 && <HeroSection items={heroItems} />}
            {coreItems.length > 0 && <YenCoreSection items={coreItems} />}
            {(heroItems.length > 0 || coreItems.length > 0) && artistItems.length > 0 && (
              <div style={{ height: "32px" }} />
            )}
            {artistItems.length > 0 && (
              <ArtistSection
                items={artistItems}
                artists={artists}
                activeArtist={activeArtist}
                onSelectArtist={setActiveArtist}
              />
            )}
            {items.length === 0 && (
              <p style={{ fontFamily: F, fontSize: "9px", letterSpacing: "0.3em", textTransform: "uppercase", opacity: 0.18, padding: "120px 32px", textAlign: "center" }}>
                No items.
              </p>
            )}
          </>
        )}
        <div style={{ height: "80px" }} />
      </div>
    </>
  );
}
